import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Checkbox, Flex, Space, Row, Col, Button, Spin, Result } from "antd";
import { InfoCircleOutlined, WarningOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import BasicSelect from "@/components/BasicComponents/BasicSelect";
import MultiLanguageRichText from "@/components/BasicComponents/MultiLanguageRichText";
import BasicLabel from "@/components/BasicComponents/BasicLabel";
import BasicDatePicker from "@/components/BasicComponents/BasicDatePicker";

import { localTimeToUtc } from "@/utils/dates";
import { getServiceMessage, postServiceMessage } from "@/utils/fetchFunctions/serviceMessage";

import { GoBackContext } from "@/context/goBackContext";

import useNotification from '@/hooks/notifications';
import useRunOnFirstMount from "@/hooks/useRunOnFirstMount";


const buttonsStyle = {
	width: 200,
	padding: '5px 10px',
	borderRadius: '100px'
};

export default function ServiceMessage () {
  const navigate = useNavigate();
  const { areChanges, setAreChanges } = useContext(GoBackContext);
  const {openNotification} = useNotification();

  const [loading, setLoading] = useState(false);
  const [succeed, setSucceed] = useState(false);
  const [countryCode, setCountryCode] = useState('en_GB');
  const [data, setData] = useState({
    messageType: 3,
    description: {},
    fromDate: null,
    toDate: null,
    onlyEnglish: false,
  });

  useRunOnFirstMount(async () => {
    setLoading(true);
		const {error, data, message} = await getServiceMessage();

    if (error) {
      handleError(message);
    } else {
      setData(data);
    }

    setLoading(false);
	});

  function handleError (error) {
    openNotification({message: 'Something went wrong!', description: error || null, type: 'error'});
  }

  function handleDescriptionChange ({countryCode, value}) {
    if (value === "<p><br></p>") {
      value = null;
    }

    setData((prev) => ({
      ...prev,
      description: {
        ...prev.description,
        [countryCode]: value
      }}));
      setAreChanges(true);
  }

  function handleDataChange (key) {
    return (value) => {
      if (key === 'fromDate' || key === 'toDate') {
        value = transformDate(value);
      }

      if (key === 'fromDate' && value === null) {
        setData((prev) => ({
          ...prev,
          toDate: null
        }));
      }

      setData((prev) => ({
        ...prev,
        [key]: value
      }));

      setAreChanges(true);
    };
  }

  function transformDate(date) {
    return date ? localTimeToUtc(date).format() : null;
  }

  function goBack () {
    navigate(-1);
	}

	async function hanldeUpload () {
    const {error, message} = await postServiceMessage(data);

    console.log({error, message});

    if (error) {
      handleError(message);
    } else {
      setAreChanges(false);
      setSucceed(true);
    }
	}

  if (succeed) {
		return (
			<Result
				status="success"
				title="Service message updated successfully"
				extra={[
					<Button type="primary" key="back" onClick={goBack}>
						Go Back
					</Button>
				]}
			/>
		);
	}

  return (
    <Spin spinning={loading}>
    <Space size="large" direction='vertical' style={{width: '100%'}}>
			<h1>{`Homepage > Service Message`}</h1>
      <Flex gap="large" vertical style={{width: '100%'}} >
        <BasicSelect
          title="Type of message"
          options={[
            {
              label: <><InfoCircleOutlined style={{color: '#1890ff'}}/> Info</>,
              value: 3,
            },
            {
              label: <><WarningOutlined style={{color: '#faad14'}}/> Warning</>,
              value: 2,
            },
            {
              label: <><ExclamationCircleOutlined style={{color: '#ff4d4f'}}/> Error</>,
              value: 1,
            },
          ]}
          onChange={handleDataChange('messageType')}
          compact
          value={data.messageType}
        />
        <MultiLanguageRichText
          title="Message"
          value={data.description}
          onChange={handleDescriptionChange}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
        />
        <BasicLabel title="Message display" />
        <Alert
					description={
						<> <InfoCircleOutlined style={{color: '#1890ff', marginRight: 10}}/> If the message is selected to be in English only, this will prevent the default display in that language for other languages.</>}
					type="info"
					/* style={{marginBottom: 20}} */
        />
        <Checkbox checked={data.onlyEnglish} onChange={(e) => handleDataChange('onlyEnglish')(e.target.checked)}>
          Display only for English
        </Checkbox>
        <div style={{marginTop: 20}}>
          <h3>Publishing Dates</h3>
          <Row gutter={[16, 24]} justify="start">
            <Col className="gutter-row" md={24} lg={12} xl={8}>
                <BasicDatePicker
                  title="Start date"
                  value={data.fromDate}
                  onChange={handleDataChange('fromDate')}
                  maxDate={data.toDate}
                />
            </Col>
            <Col className="gutter-row" md={24} lg={12} xl={8}>
                <BasicDatePicker
                  title="End date"
                  value={data.toDate}
                  onChange={handleDataChange('toDate')}
                  minDate={data.fromDate}
                  disabled={!data.fromDate}
                />
            </Col>
          </Row>
        </div>
        <div style={{marginTop: 50}}>
          <Row justify="center" gutter={[64, 16]}>
            <Col>
              <Button
                onClick={hanldeUpload}
                type="primary"
                size='large'
                style={buttonsStyle}
                disabled={!areChanges}
              >
                SAVE
              </Button>
            </Col>
            <Col>
              <Button
                onClick={goBack}
                danger type="primary"
                size='large'
                style={buttonsStyle}
              >
                GO BACK
              </Button>
            </Col>
          </Row>
        </div>
      </Flex>
		</Space>
    </Spin>
  );
}